import type { IndexedPublicPlanPrices } from '@/lib/public-plan-prices-snapshot';
import {
  formatPublicIncludedUnits,
  formatPublicPlanOverage,
  formatPublicPlanPrice,
} from '@/lib/public-plan-prices';

export type SelectablePlanSlug = 'hobby' | 'basic' | 'pro';
export type PlanSlug = SelectablePlanSlug | 'custom';

export interface PricingPlan {
  slug: PlanSlug;
  name: string;
  price: string;
  priceSuffix?: string;
  description: string;
  includedRequests: string;
  overage: string;
  workers: string;
  support: string;
  extras: string[];
  highlighted?: boolean;
  buttonText: string;
  buttonLink: string;
  notice?: string;
}

export function resolvePricingPlanSlug(value?: string | null): PlanSlug | undefined {
  if (!value) {
    return undefined;
  }

  const normalized = value.toLowerCase();
  if (normalized === 'free' || normalized === 'hobby') {
    return 'hobby';
  }
  if (normalized === 'basic' || normalized === 'pro') {
    return normalized as PlanSlug;
  }
  if (normalized === 'custom' || normalized === 'enterprise') {
    return 'custom';
  }
  return undefined;
}

export function buildPricingPlans(options: {
  isAuthenticated: boolean;
  publicPlanPrices: IndexedPublicPlanPrices;
}): PricingPlan[] {
  const { isAuthenticated, publicPlanPrices } = options;
  const basicPrice = publicPlanPrices.basic;
  const proPrice = publicPlanPrices.pro;

  return [
    {
      slug: 'hobby',
      name: 'Хобби',
      price: '0 ₽',
      description: 'Тариф с проверкой карты для тестирования платформы',
      includedRequests: '100 запросов / месяц (жесткий лимит)',
      overage: 'Недоступно',
      workers: '1 параллельный запуск',
      support: 'Справка',
      extras: ['Логи запросов за 90 дней'],
      buttonText: isAuthenticated ? 'Текущий тариф' : 'Начать бесплатно',
      buttonLink: isAuthenticated ? '/dashboard' : '/signup',
      notice: 'Для проверки нужна действующая карта. На тарифе Хобби списаний не будет.',
    },
    {
      slug: 'basic',
      name: 'Базовый',
      price: formatPublicPlanPrice(basicPrice),
      priceSuffix: '/месяц',
      description: 'Для стабильной нагрузки с поддержкой JSON-шаблонов',
      includedRequests: formatPublicIncludedUnits(basicPrice.included_units),
      overage: formatPublicPlanOverage(basicPrice),
      workers: '5 параллельных запусков',
      support: 'Поддержка по электронной почте',
      extras: ['JSON-шаблоны и JSON Schema'],
      buttonText: isAuthenticated ? 'Перейти на Базовый' : 'Начать с Базового',
      buttonLink: isAuthenticated ? '/upgrade?plan=basic' : '/signup?plan=basic',
    },
    {
      slug: 'pro',
      name: 'Про',
      price: formatPublicPlanPrice(proPrice),
      priceSuffix: '/месяц',
      description: 'Большинство клиентов выбирают Про — повышенная пропускная способность и выбор страны прокси',
      includedRequests: formatPublicIncludedUnits(proPrice.included_units),
      overage: formatPublicPlanOverage(proPrice),
      workers: '20 параллельных запусков',
      support: 'Приоритетная поддержка по электронной почте',
      extras: ['Выбор прокси по стране (10–20 стран)', 'JSON-шаблоны и JSON Schema'],
      highlighted: true,
      buttonText: isAuthenticated ? 'Перейти на Про' : 'Начать с Про',
      buttonLink: isAuthenticated ? '/upgrade?plan=pro' : '/signup?plan=pro',
    },
    {
      slug: 'custom',
      name: 'Индивидуальный',
      price: 'По запросу',
      description: 'Индивидуальные лимиты, SLA и помощь с запуском',
      includedRequests: '500 000+ запросов / месяц',
      overage: 'Условия по договору',
      workers: 'Выделенная емкость',
      support: 'Приоритет + SLA',
      extras: [
        'Повышенные лимиты и специализированные сценарии',
        'Поддержка внедрения и помощь с запуском',
      ],
      buttonText: 'Связаться с продажами',
      buttonLink: '/contact',
    },
  ];
}
